import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { api } from "./api";
import { EditorPane } from "./components/EditorPane";
import { HistoryRail } from "./components/HistoryRail";
import { ResultsPane } from "./components/ResultsPane";
import { RunBar } from "./components/RunBar";
import { STARTER_DEFINITION } from "./defaults";
import { normalizeDefinition } from "./state/definition";
import { initialReplayState, replayReducer } from "./state/replay";
import type { PrimitivesInfo, ReplayPayload, RunStatus, SimulationDef } from "./types";

const POLL_MS = 250; // fixed delay after each response (≤4 Hz, self-throttling)
const BASE_TICKS_PER_SECOND = 4;

export function App({ readOnly = false }: { readOnly?: boolean }) {
  const [definition, setDefinition] = useState<SimulationDef>(() =>
    normalizeDefinition(STARTER_DEFINITION as never),
  );
  const [definitionName, setDefinitionName] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [ticks, setTicks] = useState(120);
  const [primitives, setPrimitives] = useState<PrimitivesInfo | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [activeRun, setActiveRun] = useState<RunStatus | null>(null);
  const [history, setHistory] = useState<RunStatus[]>([]);
  const [replay, setReplay] = useState<ReplayPayload | null>(null);
  const [replayRunId, setReplayRunId] = useState<string | null>(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayState, dispatch] = useReducer(replayReducer, initialReplayState);
  const [toasts, setToasts] = useState<{ id: number; text: string }[]>([]);
  const toastId = useRef(0);
  const replayRequest = useRef(0);

  const toast = useCallback((text: string) => {
    const id = ++toastId.current;
    setToasts((current) => [...current, { id, text }]);
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), 6000);
  }, []);

  const refreshHistory = useCallback(() => {
    api.history().then(setHistory).catch((error: unknown) => toast(String(error)));
  }, [toast]);

  // Bootstrap: vocabulary + history.
  useEffect(() => {
    api.primitives().then(setPrimitives).catch((error: unknown) => toast(String(error)));
    refreshHistory();
  }, [refreshHistory, toast]);

  // Debounced validation of the working definition (a JSON parse error short-circuits).
  // The hosted snapshot API has no /api/validate — read-only mode skips it.
  useEffect(() => {
    if (readOnly || parseError !== null) return;
    const handle = setTimeout(() => {
      api
        .validate(definition)
        .then((result) => setErrors(result.errors))
        .catch((error: unknown) => toast(String(error)));
    }, 500);
    return () => clearTimeout(handle);
  }, [definition, parseError, readOnly, toast]);

  const loadReplay = useCallback(
    async (runId: string) => {
      const request = ++replayRequest.current;
      dispatch({ type: "pause" }); // never let an old play timer walk ticks during a load
      setReplayLoading(true);
      try {
        const payload = await api.replay(runId);
        if (request !== replayRequest.current) return; // a newer load superseded this one
        setReplay(payload);
        setReplayRunId(runId);
        dispatch({
          type: "loaded",
          maxTick: payload.frames.length ? payload.frames[payload.frames.length - 1].tick : 0,
        });
      } catch (error) {
        if (request === replayRequest.current) toast(String(error));
      } finally {
        if (request === replayRequest.current) setReplayLoading(false);
      }
    },
    [toast],
  );

  // Poll the active run.
  useEffect(() => {
    if (!activeRun || (activeRun.status !== "queued" && activeRun.status !== "running")) return;
    const interval = setInterval(() => {
      api
        .runStatus(activeRun.id)
        .then((status) => {
          setActiveRun(status);
          if (status.status === "done") {
            void loadReplay(status.id);
            refreshHistory();
          } else if (status.status === "failed" || status.status === "cancelled") {
            if (status.error) toast(`run ${status.id} failed: ${status.error}`);
            refreshHistory();
          }
        })
        .catch((error: unknown) => {
          toast(String(error));
          setActiveRun(null);
        });
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [activeRun, loadReplay, refreshHistory, toast]);

  // The play timer advances the shared tick; every view follows.
  useEffect(() => {
    if (!replayState.playing) return;
    const interval = setInterval(
      () => dispatch({ type: "advance" }),
      1000 / (BASE_TICKS_PER_SECOND * replayState.speed),
    );
    return () => clearInterval(interval);
  }, [replayState.playing, replayState.speed]);

  const editDefinition = useCallback((next: SimulationDef) => {
    setDefinition(next);
    setDirty(true);
  }, []);

  const openDefinition = useCallback((raw: Record<string, unknown>, name: string | null) => {
    setDefinition(normalizeDefinition(raw as never));
    setDefinitionName(name);
    setDirty(false);
    setParseError(null);
  }, []);

  const run = useCallback(async () => {
    // A new run owns the results pane: drop the previous replay and stop playback.
    replayRequest.current++;
    dispatch({ type: "pause" });
    setReplay(null);
    setReplayRunId(null);
    setReplayLoading(false);
    try {
      const { id } = await api.startRun(definition, ticks, definition.name);
      setActiveRun({
        id,
        status: "queued",
        tick: 0,
        ticks,
        name: definition.name,
        seed: definition.seed,
      });
    } catch (error) {
      toast(String(error));
    }
  }, [definition, ticks, toast]);

  const cancel = useCallback(() => {
    if (!activeRun) return;
    api.cancelRun(activeRun.id).catch((error: unknown) => toast(String(error)));
  }, [activeRun, toast]);

  const openRunDefinition = useCallback(
    (runId: string) => {
      api
        .runDefinition(runId)
        .then((raw) => openDefinition(raw, null))
        .catch((error: unknown) => toast(String(error)));
    },
    [openDefinition, toast],
  );

  const running =
    activeRun !== null && (activeRun.status === "queued" || activeRun.status === "running");
  const canRun = !readOnly && parseError === null && errors.length === 0 && !running;
  const digest = activeRun?.digest ?? replay?.manifest.digest ?? null;

  return (
    <div className="studio">
      <RunBar
        readOnly={readOnly}
        definition={definition}
        definitionName={definitionName}
        dirty={dirty}
        ticks={ticks}
        onTicks={setTicks}
        onSeed={(seed) => editDefinition({ ...definition, seed })}
        onName={(name) => editDefinition({ ...definition, name })}
        activeRun={activeRun}
        canRun={canRun}
        onRun={() => void run()}
        onCancel={cancel}
        digest={digest}
        onToast={toast}
      />
      <main className="studio-main">
        <EditorPane
          readOnly={readOnly}
          definition={definition}
          definitionName={definitionName}
          dirty={dirty}
          primitives={primitives}
          errors={errors}
          parseError={parseError}
          onEdit={editDefinition}
          onParseError={setParseError}
          onOpen={openDefinition}
          onToast={toast}
        />
        <ResultsPane
          replay={replay}
          loading={replayLoading}
          state={replayState}
          dispatch={dispatch}
          runId={replayRunId}
        />
      </main>
      <HistoryRail
        history={history}
        onLoad={(id) => void loadReplay(id)}
        onOpenDefinition={openRunDefinition}
      />
      <div className="toasts" role="status">
        {toasts.map((t) => (
          <div key={t.id} className="toast">
            {t.text}
          </div>
        ))}
      </div>
    </div>
  );
}
